import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useAdminGhosting } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { subscriptionKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import PaymentsSubscriptionsDetail from './scenes/PaymentsSubscriptionsDetail';
import PaymentsSubscriptionsDetailPayments from './scenes/PaymentsSubscriptionsDetailPayments';
import SubscriptionsDetailEmailLogs from './scenes/SubscriptionsDetailEmailLogs';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import NavTabs from '@dashboard/components/NavTabs';
import SubscriptionStatusBadge from '@/dashboard/components/SubscriptionStatusBadge/SubscriptionStatusBadge';
import isRecoveredAbandonment from '@/dashboard/services/abandonment/isRecoveredAbandonment';
import isAbandonedAbandonment from '@/dashboard/services/abandonment/isAbandonedAbandonment';
import Tooltip from '@/dashboard/components/Tooltip';
import { formatDateTime } from '@/dashboard/lib/format-date';
import StatusBadge from '@/dashboard/components/StatusBadge';
import { TrackRecent } from '@dashboard/components/CommandPalette/TrackRecent';
import { OrderPageHeader, OrderPageSkeleton } from '@dashboard/components/OrderPage';
import { getPageTitle } from '@dashboard/lib/pageTitle';

const getTabs = ({ base, subscriptionId, subscription, refetch }) => [
  {
    name: 'Subscription',
    href: `${base}`,
    render: props => (
      <PaymentsSubscriptionsDetail
        {...props}
        subscriptionId={subscriptionId}
        subscription={subscription}
        refetch={refetch}
      />
    ),
  },
  {
    name: 'Emails',
    href: `${base}/emails`,
    render: props => (
      <SubscriptionsDetailEmailLogs
        {...props}
        subscriptionId={subscriptionId}
        subscription={subscription}
      />
    ),
  },
  {
    name: 'Payments',
    href: `${base}/payments`,
    render: props => (
      <PaymentsSubscriptionsDetailPayments
        {...props}
        subscriptionId={subscriptionId}
        subscription={subscription}
      />
    ),
  },
];

const PaymentsSubscriptionsDetailOverview = () => {
  const { token, sellerId } = useAuth();
  const { setAdminGhostingSellerId } = useAdminGhosting();
  const {
    url,
    params: { subscriptionId },
  } = useRouteMatch();
  const location = useLocation();

  const {
    data: subscription,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: subscriptionKey(subscriptionId),
    queryFn: () => fetcher(`/sellers/${sellerId}/subscriptions/${subscriptionId}`, token),
  });

  React.useEffect(() => {
    if (subscription?.seller) {
      setAdminGhostingSellerId(subscription.seller);
    }
  }, [subscription?.seller, setAdminGhostingSellerId]);

  if (isLoading) {
    return <OrderPageSkeleton tabs={4} />;
  }

  if (!subscription || error) {
    return (
      <Card variant="root" contentColumn>
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  const tabs = getTabs({ base: url, subscriptionId, subscription, refetch: refetch });

  const isRecovered = isRecoveredAbandonment(subscription);

  const isAbandoned = isAbandonedAbandonment(subscription);
  return (
    <Card variant="root" contentColumn>
      <TrackRecent
        type="subscription"
        id={subscription._id}
        label={getPageTitle(subscription.page) || subscription._id}
        subtitle={getPriceLabel({
          amount: subscription.amount,
          currency: subscription.currency,
          interval: subscription.interval,
          intervalCount: subscription.intervalCount,
          type: 'subscription',
        })}
        path={`/orders/subscriptions/${subscription._id}`}
      />
      <CardHeader variant="root">
        <OrderPageHeader
          title={subscription.orderId ? `Subscription #${subscription.orderId}` : 'Subscription'}
          badges={
            isRecovered ? (
              <>
                <Tooltip label={`Recovered on ${formatDateTime(subscription.recoveredAt)}`}>
                  <StatusBadge status="recovered" label="Recovered" withInfoIcon />
                </Tooltip>
                <SubscriptionStatusBadge
                  status={subscription.status}
                  livemode={subscription.livemode}
                  error={subscription.paymentError}
                />
              </>
            ) : isAbandoned ? (
              <>
                <SubscriptionStatusBadge status="abandoned" livemode={subscription.livemode} />
                <SubscriptionStatusBadge
                  status={subscription.status}
                  livemode={subscription.livemode}
                  error={subscription.paymentError}
                />
              </>
            ) : (
              <SubscriptionStatusBadge
                status={subscription.status}
                livemode={subscription.livemode}
                error={subscription.paymentError}
              />
            )
          }
          date={formatDateTime(subscription.createdAt)}
        />

        <NavTabs aria-label="Subscription detail">
          {tabs.map(tab => (
            <NavTabs.Item key={tab.name} to={tab.href} active={location.pathname === tab.href}>
              {tab.name}
            </NavTabs.Item>
          ))}
        </NavTabs>
      </CardHeader>

      <CardContent variant="compact">
        <Switch>
          {tabs.map(tab => (
            <Route key={tab.href} path={tab.href} render={tab.render} exact />
          ))}
        </Switch>
      </CardContent>
    </Card>
  );
};

export default PaymentsSubscriptionsDetailOverview;
